import { makeAutoObservable } from "mobx";
import { filterOrgTreeByIds } from "../utils/orgTree.helpers";
import { normalizeOrgId } from "@/features/esg/utils/org-scope.utils";
import { disclosureStore } from "./disclosure.store";

class EntryDataStore {
  standardEntries = null;
  subEntries = null;
  computedEntries = null;
  orgList = [];
  orgTree = [];
  filteredOrgTree = [];
  // True once the org hierarchy fetch has succeeded and the trees above
  // reflect the real scope. Consumers that treat an empty tree as "still
  // loading" must gate on this instead: after load, an empty filtered or
  // disclosure tree is a genuine zero-scope, not a pending fetch.
  orgTreeLoaded = false;
  // id → { name, isActive }. Includes archived orgs so name lookups for
  // existing DataEntry rows don't fall back to the raw ObjectId.
  orgLookupMap = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  get disclosureOrgTree() {
    const disclosure = disclosureStore.activeDisclosure;

    if (!disclosure) return this.filteredOrgTree;

    // General category: parent company only
    if (disclosure.category === "General")
      return this.filteredOrgTree.map((node) => ({ ...node, children: [] }));

    if (disclosure.isAllOrganization) return this.filteredOrgTree;

    const orgIds = disclosure.organizationIds || [];

    if (!orgIds.length && !disclosure.isCompanySelected)
      return this.filteredOrgTree;

    const allowedSet = new Set(
      orgIds.map((id) => normalizeOrgId(id)).filter(Boolean),
    );

    if (disclosure.isCompanySelected) {
      this.filteredOrgTree.forEach((node) => {
        if (node.id) allowedSet.add(normalizeOrgId(node.id));
      });
    }

    if (!allowedSet.size) return this.filteredOrgTree;

    return filterOrgTreeByIds(this.filteredOrgTree, allowedSet);
  }

  targets = [];

  setTargets = (targets) => (this.targets = targets);

  getTarget = (organizationId, year) => {
    return this.targets.find((t) => {
      const tOrgId = t.organizationId?._id || t.organizationId || null;
      const matchOrg =
        tOrgId === organizationId || (!tOrgId && !organizationId);
      return matchOrg && t.year === Number(year);
    });
  };

  setStandardEntries = (entries) => (this.standardEntries = entries);

  setSubEntries = (entries) => (this.subEntries = entries);

  setComputedEntries = (entries) => (this.computedEntries = entries);

  setOrgList = (orgList) => (this.orgList = orgList);

  setOrgTree = (orgTree) => (this.orgTree = orgTree);

  setFilteredOrgTree = (tree) => (this.filteredOrgTree = tree);

  setOrgTreeLoaded = (v) => (this.orgTreeLoaded = v);

  setOrgLookupMap = (orgs = []) => {
    this.orgLookupMap.clear();
    for (const o of orgs) {
      const id = o?._id ?? o?.id;
      if (!id) continue;
      this.orgLookupMap.set(String(id), {
        name: o.name,
        isActive: o.isActive !== false,
      });
    }
  };

  reset = () => {
    this.standardEntries = null;
    this.subEntries = null;
    this.computedEntries = null;
    this.targets = [];
    this.orgList = [];
    this.orgTree = [];
    this.filteredOrgTree = [];
    this.orgTreeLoaded = false;
    this.orgLookupMap.clear();
  };
}

export const entryDataStore = new EntryDataStore();
